import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Users, Search, CheckCircle2 } from 'lucide-react';
import { ApiError } from '../../lib/api';
import {
  useTrainingAssignees,
  useAssignTrainingModule,
  type TrainingModuleSummary,
} from '../../hooks/useTrainingModules';
import { useTrainingProgress } from '../../hooks/useTrainingProgress';
import { ModalShell } from '../ui/ModalShell';
import { Button } from '../ui/Button';
import { Pill } from '../ui/Pill';
import { SkeletonList } from '../ui/Skeleton';

/**
 * Tick-box assignment: pick people and assign one module to them. People
 * already on the module are shown with their status and locked (assigning is
 * idempotent, but checking them would imply you could un-assign, which is a
 * separate reopen/remove flow). Includes auto-provisioned learners.
 */
export function AssignPeopleModal({
  module,
  open,
  onClose,
}: {
  module: TrainingModuleSummary;
  open: boolean;
  onClose: () => void;
}) {
  const assignees = useTrainingAssignees();
  // Who already has this module (and their status), so we don't re-offer them.
  const progress = useTrainingProgress({ module_id: module.id });
  const assign = useAssignTrainingModule();

  const [query, setQuery] = useState('');
  const [checked, setChecked] = useState<Set<string>>(new Set());

  const assignedStatus = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of progress.data?.people ?? []) {
      const cell = Object.values(row.cells)[0];
      map.set(row.person.id, cell?.status ?? 'not_started');
    }
    return map;
  }, [progress.data]);

  const people = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = assignees.data ?? [];
    if (!q) return all;
    return all.filter(
      (p) =>
        p.full_name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q),
    );
  }, [assignees.data, query]);

  function toggle(id: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function onAssign() {
    const ids = [...checked];
    if (ids.length === 0) return;
    try {
      const r = await assign.mutateAsync({ id: module.id, person_ids: ids });
      toast.success(
        r.assigned > 0
          ? `Assigned to ${r.assigned} ${r.assigned === 1 ? 'person' : 'people'}`
          : 'Everyone selected was already assigned',
      );
      setChecked(new Set());
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not assign');
    }
  }

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      titleId="assign-people-title"
      maxWidth="max-w-lg"
      busy={assign.isPending}
    >
      <div className="mb-3 flex items-center gap-2">
        <Users className="h-5 w-5 text-accent" />
        <h2 id="assign-people-title" className="text-base font-semibold text-fg">
          Assign “{module.title}”
        </h2>
      </div>

      <label className="mb-3 flex items-center gap-2 rounded-token-sm border border-line-strong/15 bg-surface-2 px-2.5 py-1.5">
        <Search className="h-4 w-4 text-fg-dim" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search people"
          className="w-full bg-transparent text-sm text-fg outline-none"
        />
      </label>

      <div className="max-h-80 overflow-y-auto rounded-token-sm border border-line/10">
        {assignees.isLoading ? (
          <div className="p-3">
            <SkeletonList rows={5} />
          </div>
        ) : people.length === 0 ? (
          <p className="p-4 text-sm text-fg-muted">No people match.</p>
        ) : (
          <ul className="divide-y divide-divider/10">
            {people.map((p) => {
              const status = assignedStatus.get(p.id);
              const alreadyAssigned = status !== undefined;
              return (
                <li key={p.id}>
                  <label
                    className={`flex items-center gap-3 px-3 py-2 text-sm ${
                      alreadyAssigned ? 'opacity-70' : 'cursor-pointer hover:bg-surface-2'
                    }`}
                  >
                    <input
                      type="checkbox"
                      disabled={alreadyAssigned || assign.isPending}
                      checked={alreadyAssigned || checked.has(p.id)}
                      onChange={() => toggle(p.id)}
                      className="h-4 w-4 rounded border-line-strong/30 text-accent"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-fg">{p.full_name}</span>
                      <span className="block truncate text-xs text-fg-muted">
                        {p.position ?? p.email}
                      </span>
                    </span>
                    {alreadyAssigned ? (
                      <Pill
                        tone={status === 'passed' ? 'success' : 'neutral'}
                        size="sm"
                        icon={status === 'passed' ? <CheckCircle2 className="h-3 w-3" /> : undefined}
                      >
                        {status === 'passed'
                          ? 'Passed'
                          : status === 'in_progress'
                            ? 'In progress'
                            : 'Assigned'}
                      </Pill>
                    ) : null}
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between gap-2">
        <span className="text-xs text-fg-muted">
          {checked.size} selected
        </span>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={assign.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onAssign}
            loading={assign.isPending}
            disabled={checked.size === 0}
          >
            Assign{checked.size > 0 ? ` ${checked.size}` : ''}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}
